import { ArrowRight, Search, ShieldAlert, ShieldCheck, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "../app/router";
import { PageMeta } from "../components/PageMeta";
import { PlantArt } from "../components/PlantArt";
import { plants } from "../data/catalog";
import { careLabels, lightLabels, plantGroups } from "../data/taxonomy";
import type { Care, Light, Plant } from "../data/model";
import { formatMoney } from "../lib/format";

export function PlantsPage() {
  const [query, setQuery] = useState("");
  const [light, setLight] = useState<Light | "all">("all");
  const [care, setCare] = useState<Care | "all">("all");
  const [petSafe, setPetSafe] = useState(false);
  const [selected, setSelected] = useState<Plant | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!selected) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    };
  }, [selected]);

  const filtered = useMemo(
    () =>
      plants.filter((plant) => {
        const text = `${plant.name} ${plant.scientificName}`.toLocaleLowerCase("vi");
        return (
          text.includes(query.toLocaleLowerCase("vi")) &&
          (light === "all" || plant.lights.includes(light)) &&
          (care === "all" || plant.care === care) &&
          (!petSafe || plant.petSafety === "safe")
        );
      }),
    [care, light, petSafe, query],
  );

  const reset = () => {
    setQuery("");
    setLight("all");
    setCare("all");
    setPetSafe(false);
  };

  return (
    <>
      <PageMeta
        title="Thư viện cây ban công | Rêu Nắng"
        description="Tra cứu ánh sáng, công chăm và độ an toàn thú cưng của cây dùng trong demo."
      />
      <section className="page-intro plant-intro">
        <div className="container intro-split">
          <div>
            <span className="eyebrow">12 cây · 4 nhóm · dữ liệu cố định</span>
            <h1>Tìm cây theo nơi nó sẽ sống.</h1>
          </div>
          <p className="lead">
            Ảnh là minh họa đồ họa, không dùng để định danh. Tên khoa học và nhãn an toàn giúp bạn tra cứu tiếp trước khi mua.
          </p>
        </div>
      </section>
      <section className="section plant-library-section">
        <div className="container plant-library-layout">
          <aside className="plant-filters" aria-label="Bộ lọc cây">
            <h2>Lọc thư viện</h2>
            <label className="search-field">
              <span className="sr-only">Tìm theo tên cây</span>
              <Search aria-hidden="true" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Tên cây hoặc tên khoa học" />
            </label>
            <fieldset>
              <legend>Ánh sáng</legend>
              <div className="filter-options">
                <label><input type="radio" name="light" checked={light === "all"} onChange={() => setLight("all")} /> Tất cả</label>
                {(Object.keys(lightLabels) as Light[]).map((key) => (
                  <label key={key}><input type="radio" name="light" checked={light === key} onChange={() => setLight(key)} /> {lightLabels[key]}</label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend>Công chăm</legend>
              <select value={care} onChange={(event) => setCare(event.target.value as Care | "all")}>
                <option value="all">Tất cả mức chăm</option>
                {(Object.keys(careLabels) as Care[]).map((key) => <option value={key} key={key}>{careLabels[key]}</option>)}
              </select>
            </fieldset>
            <label className="switch-row">
              <input type="checkbox" checked={petSafe} onChange={(event) => setPetSafe(event.target.checked)} />
              <span><strong>Chỉ cây pet-safe</strong><small>Loại cây có nhãn caution/unsafe</small></span>
            </label>
            <button className="text-button" type="button" onClick={reset}>Xóa bộ lọc</button>
          </aside>
          <div className="plant-results">
            <div className="results-top">
              <span aria-live="polite">{filtered.length} cây phù hợp</span>
              <div className="group-key" aria-label="Các nhóm cây">
                {plantGroups.map((group) => <span key={group.id}>{group.name}</span>)}
              </div>
            </div>
            {filtered.length ? (
              <div className="plant-grid">
                {filtered.map((plant) => (
                  <button
                    className="plant-card"
                    type="button"
                    key={plant.id}
                    onClick={(event) => {
                      triggerRef.current = event.currentTarget;
                      setSelected(plant);
                    }}
                  >
                    <PlantArt mediaKey={plant.mediaKey} label={plant.name} />
                    <span className="plant-card-copy">
                      <span className="plant-badges">
                        <i>{plant.lights.map((entry) => lightLabels[entry]).slice(0, 2).join(" · ")}</i>
                        <i className={plant.petSafety === "safe" ? "safe" : "caution"}>
                          {plant.petSafety === "safe" ? "Pet-safe" : "Cần lưu ý"}
                        </i>
                      </span>
                      <strong>{plant.name}</strong>
                      <em>{plant.scientificName}</em>
                      <small>{formatMoney(plant.unitPrice)}</small>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <LeafIcon />
                <h2>Chưa có cây nào khớp cả ba bộ lọc.</h2>
                <p>Thử nới mức chăm hoặc xóa từ khóa.</p>
                <button className="button button-secondary" type="button" onClick={reset}>Xóa bộ lọc</button>
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="section library-cta">
        <div className="container compact-cta">
          <div><span className="eyebrow">Biết cây là một nửa</span><h2>Để công cụ ghép cây vào đúng diện tích.</h2></div>
          <Link className="button button-primary" to="/thiet-ke">Mở Xưởng ban công <ArrowRight aria-hidden="true" /></Link>
        </div>
      </section>

      {selected && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSelected(null);
        }}>
          <section className="plant-dialog" role="dialog" aria-modal="true" aria-labelledby="plant-dialog-title">
            <button ref={closeRef} className="icon-button dialog-close" type="button" aria-label="Đóng chi tiết cây" onClick={() => setSelected(null)}>
              <X aria-hidden="true" />
            </button>
            <PlantArt mediaKey={selected.mediaKey} label={selected.name} />
            <div className="plant-dialog-copy">
              <span className="eyebrow">{plantGroups.find((group) => group.id === selected.groupId)?.name}</span>
              <h2 id="plant-dialog-title">{selected.name}</h2>
              <p className="latin-name">{selected.scientificName}</p>
              <p>{selected.description}</p>
              <dl>
                <div><dt>Ánh sáng</dt><dd>{selected.lights.map((entry) => lightLabels[entry]).join(", ")}</dd></div>
                <div><dt>Chăm</dt><dd>{careLabels[selected.care]} · tưới {selected.wateringPerWeek} lần/tuần</dd></div>
                <div><dt>Thú cưng</dt><dd className={selected.petSafety === "safe" ? "safe-text" : "warning-text"}>{selected.petSafety === "safe" ? <ShieldCheck aria-hidden="true" /> : <ShieldAlert aria-hidden="true" />} {selected.petSafety === "safe" ? "Được xếp pet-safe trong demo" : "Không tự đề xuất khi nhà có thú cưng"}</dd></div>
                <div><dt>Giá cây</dt><dd>{formatMoney(selected.unitPrice)}</dd></div>
              </dl>
              <div className="care-note"><strong>Ghi chú chăm</strong><p>{selected.careNote}</p></div>
              <small className="disclaimer-small">Ngay cả cây được xếp không độc vẫn có thể gây khó chịu nếu vật nuôi ăn phải. Hãy hỏi bác sĩ thú y khi cần.</small>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function LeafIcon() {
  return <span className="empty-leaf" aria-hidden="true">⌁</span>;
}
